import React, { useEffect, useState, useRef } from "react";
import "./department-info.scss";
import AdminNavigation from "@components/Admin/admin-navigation";
import { toast } from "react-toastify";
import InputField from "@common/InputRuleFeild";
import { getValue, setValue } from "@utils/lodash";
import SimpleReactValidator from "simple-react-validator";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@layouts/withoutNaviagtion/withoutNavigationLayout";
import _ from "lodash";
import PromptModal from "@prompt/modal";
import { useCallbackPrompt } from "@prompt/useCallbackPrompt";
import GearLoader from "@components/common/GearLoader";
import { StylesConfig } from "react-select";
import {
  createDepartment,
  getSpecificDepartment,
  updateDepartment,
} from "@services/departments.service";
import SingleSelectSearchDropdown from "@components/common/CustomSearchableDropdown/SingleSelectSearchDropdown";
import NoAccessPage from "@components/common/NoAccess";
import RemoveCircleSvgComponent from "@assets/svg-components/remove-circle";

interface DepartmentLocationHeadFormValue {
  city_id: string | number;
  city_name?: string;
  head_user_id: string | number;
}

interface DepartmentFormValue {
  department_name: string;
  department_code: string;
  description: string;
  department_head_id: string | number;
  department_location_heads: DepartmentLocationHeadFormValue[];
}

const getDefaultLocationHead = () => ({
  city_id: "",
  head_user_id: "",
});

interface IAdminUserControlUserDetailsProps {}

const AdminUserControlDepartmentDetails: React.FunctionComponent<
  IAdminUserControlUserDetailsProps
> = (props: any) => {
  let orgParams = useParams();
  let params = useParams();
  let navigate = useNavigate();

  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */
  const simpleValidator = useRef(new SimpleReactValidator());
  const [, forceUpdate] = useState(0);
  const [request, setRequest] = useState<DepartmentFormValue>({
    department_name: "",
    department_code: "",
    description: "",
    department_head_id: 0,
    department_location_heads: [],
  });

  const [dirtyFields, setDirtyFields] = useState<DepartmentFormValue>({
    department_name: "",
    department_code: "",
    description: "",
    department_head_id: 0,
    department_location_heads: [],
  });

  const reset = () => {
    setRequest({
      department_name: "",
      department_code: "",
      description: "",
      department_head_id: 0,
      department_location_heads: [],
    });
    setDirtyFields({
      department_name: "",
      department_code: "",
      description: "",
      department_head_id: 0,
      department_location_heads: [],
    });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    if (props.permissionAPITriggered) {
      if (getValue(params, `id`, "")) {
        getDepartment(getValue(params, `id`, ""));
      } else {
        setLoading(false);
      }
    }
  }, [props.permissionAPITriggered]);
  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getDepartment = async (id: string) => {
    try {
      setLoading(true);
      let resp = await getSpecificDepartment(id);
      if (resp) {
        const departmentLocationHeads = getValue(
          resp,
          `data.location_heads`,
          getValue(resp, `data.department_location_heads`, [])
        ).map((item: any) => ({
          city_id: getValue(item, `city.id`, ""),
          city_name: getValue(item, `city.name`, ""),
          head_user_id: getValue(
            item,
            `department_head.id`,
            getValue(item, `head.id`, "")
          ),
        }));

        setRequest({
          department_name: getValue(resp, `data.department_name`, ""),
          department_code: getValue(resp, `data.department_code`, ""),
          description: getValue(resp, `data.description`, ""),
          department_head_id: getValue(resp, `data.department_head.id`, ""),
          department_location_heads: departmentLocationHeads,
        });
        setDirtyFields({
          department_name: getValue(resp, `data.department_name`, ""),
          department_code: getValue(resp, `data.department_code`, ""),
          description: getValue(resp, `data.description`, ""),
          department_head_id: getValue(resp, `data.department_head.id`, ""),
          department_location_heads: departmentLocationHeads,
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const formValid = simpleValidator.current.allValid();
    const locationHeads = getValue(request, `department_location_heads`, []);
    const incompleteLocationRow = locationHeads.some(
      (item: any) =>
        (getValue(item, `city_id`, "") && !getValue(item, `head_user_id`, "")) ||
        (!getValue(item, `city_id`, "") && getValue(item, `head_user_id`, ""))
    );
    const cityIds = locationHeads
      .map((item: any) => getValue(item, `city_id`, ""))
      .filter(Boolean);
    const hasDuplicateCities = new Set(cityIds).size !== cityIds.length;

    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
    } else if (incompleteLocationRow) {
      toast.error("Please choose both city and department head for each row");
    } else if (hasDuplicateCities) {
      toast.error("Each city can be mapped only once");
    } else {
      try {
        setIsLoading(true);
        let payload = {
          department_name: getValue(request, `department_name`, ""),
          department_code: getValue(request, `department_code`, ""),
          description: getValue(request, `description`, ""),
          department_head_id: getValue(request, `department_head_id`, ""),
          department_location_heads: locationHeads.filter(
            (item: any) =>
              getValue(item, `city_id`, "") &&
              getValue(item, `head_user_id`, "")
          ),
        };
        if (getValue(params, `id`, "")) {
          let resp = await updateDepartment(
            getValue(params, `id`, ""),
            payload
          );
          if (resp) {
            setIsLoading(false);
            confirmNavigation();
            toast.success("Updated successfully");
            navigate(`/admin/user-controls/departments`);
          } else {
            setIsLoading(false);
          }
        } else {
          let resp = await createDepartment(payload);
          if (resp) {
            setIsLoading(false);
            reset();
            confirmNavigation();
            toast.success("Created successfully");
            navigate(`/admin/user-controls/departments`);
          } else {
            setIsLoading(false);
          }
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */

  const handleChangeText = (e: any) => {
    const { name, value } = e.target;
    setRequest({
      ...request,
      [name]: value,
    });
  };

  const handleAddLocationHead = () => {
    setRequest({
      ...request,
      department_location_heads: [
        ...getValue(request, `department_location_heads`, []),
        getDefaultLocationHead(),
      ],
    });
  };

  const handleRemoveLocationHead = (index: number) => {
    const locationHeads = getValue(request, `department_location_heads`, []);
    setRequest({
      ...request,
      department_location_heads: locationHeads.filter(
        (_item: object, currentIndex: number) => currentIndex !== index
      ),
    });
  };

  const handleLocationHeadChange = (
    index: number,
    field: "city_id" | "head_user_id" | "city_name",
    value: string | number
  ) => {
    const locationHeads = [...getValue(request, `department_location_heads`, [])];
    locationHeads[index] = {
      ...getValue(locationHeads, `[${index}]`, getDefaultLocationHead()),
      [field]: value,
    };
    setRequest({
      ...request,
      department_location_heads: locationHeads,
    });
  };
  /* -------------------------------------------------------------------------- */
  /*                                Prompt Section                              */
  /* -------------------------------------------------------------------------- */
  const [isChanged, setIsChanged] = useState(false);
  useEffect(() => {
    setIsChanged(
      _.isEqualWith(request, dirtyFields, (a: any, b: any) => {
        // if both are null or equal to an empty string then they are equal
        if ((_.isNull(a) || a === "") && (_.isNull(b) || b === "")) return true;
      })
    );
  });
  const [showPrompt, confirmNavigation, cancelNavigation]: any =
    useCallbackPrompt(!isChanged);

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */
  const customStyles: StylesConfig = {
    placeholder: (defaultStyles) => {
      return {
        ...defaultStyles,
        color: "#dadce0",
      };
    },
    control: (provided: Record<string, unknown>, state: any) => ({
      ...provided,
      height: 43,
      border: state.isFocused ? "1px solid #dadce0" : "1px solid #dadce0",
      boxShadow: state.isFocused ? "0px 0px 0px #dadce0" : "none",
      "&:hover": {
        // border: "1px solid #ff8b67",
        // boxShadow: "0px 0px 6px #ff8b67"
      },
    }),
  };

  return (
    <>
      {/* <Header /> */}
      <Layout>
        {!loading &&
        !getValue(props, `subTabPermissionList`, []).includes(
          "admin_user_controls_departments"
        ) ? (
          <NoAccessPage />
        ) : (
          <>
            <PromptModal
              showDialog={showPrompt}
              confirmNavigation={confirmNavigation}
              cancelNavigation={cancelNavigation}
            />
            <AdminNavigation
              id={orgParams.id}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              url={`/admin/user-controls/departments`}
              isChanged={isChanged}
              hideHeaderMenu
              permissions={getValue(props, `permissions`, [])}
            />
            {
              <div className="user-details-wrapper mt-0 department-details-wrapper">
                {loading || !props.permissionAPITriggered ? (
                  <GearLoader />
                ) : (
                  <div className="user-details-wrapper-form m-0">
                    <div className="checkpoint-active-details-wrapper__form-wrapper">
                      <div className="checkpoint-active-details-wrapper__form-content">
                        {/* <h4 className="mb-3">
                        {getValue(params, `id`, "") ? "Update" : "Add"} Location
                      </h4> */}
                        <div className="user-details-wrapper__form user-details-wrapper__form--org-profile">
                          <div className="user-details-wrapper__form-group form-group">
                            <label className="checkpoint-active-details__label form-label-error">
                              Department Name <span>*</span>
                            </label>
                            <InputField
                              inputType="TEXT"
                              placeholder="Enter Department Name"
                              className="form-control w-100"
                              name="department_name"
                              id="department_name"
                              value={getValue(request, `department_name`, "")}
                              onChange={(e: any) => handleChangeText(e)}
                              label="Department Name"
                              validator={simpleValidator}
                            />
                          </div>

                          <div className="user-details-wrapper__form-group form-group">
                            <label className="checkpoint-active-details__label form-label-error">
                              Department Code <span>*</span>
                            </label>
                            <InputField
                              inputType="TEXT"
                              placeholder="Enter Department Code"
                              className="form-control w-100"
                              name="department_code"
                              id="department_code"
                              value={getValue(request, `department_code`, "")}
                              onChange={(e: any) => handleChangeText(e)}
                              label="Department Code"
                              validator={simpleValidator}
                            />
                          </div>
                        </div>
                        <div className="user-details-wrapper__form-group form-group department-head-select-wrap">
                          <label className="checkpoint-active-details__label form-label-error">
                            Department Head <span>*</span>
                          </label>
                          <SingleSelectSearchDropdown
                            label={"label"}
                            name="Department Head"
                            value={getValue(request, `department_head_id`, "")}
                            onChange={(e: any) =>
                              setRequest({
                                ...request,
                                department_head_id: getValue(e, `id`, ""),
                              })
                            }
                            lookup_id={getValue(
                              request,
                              `department_head_id`,
                              ""
                            )}
                            placeholder={"Choose Department Head "}
                            lookup_api={"users"}
                            width={"745px"}
                            validator={simpleValidator}
                          />
                        </div>
                        <div className="department-location-heads-wrap mt-4">
                          <div className="department-location-heads-header">
                            <div>
                              <label className="checkpoint-active-details__label mb-0">
                                Location Heads
                              </label>
                              <p className="department-location-heads-subtext">
                                Assign department heads based on city
                              </p>
                            </div>
                          </div>

                          {getValue(
                            request,
                            `department_location_heads.length`,
                            0
                          ) > 0 ? (
                            <div className="department-location-heads-list">
                              {getValue(
                                request,
                                `department_location_heads`,
                                []
                              ).map((item: any, index: number) => (
                                <div
                                  className="department-location-head-row"
                                  key={index}
                                >
                                  <div className="department-location-head-field">
                                    <label className="checkpoint-active-details__label mb-1">
                                      City
                                    </label>
                                    <SingleSelectSearchDropdown
                                      label={"label"}
                                      name={`city-${index}`}
                                      value={getValue(item, `city_id`, "")}
                                      onChange={(e: any) =>
                                        setRequest({
                                          ...request,
                                          department_location_heads: getValue(
                                            request,
                                            `department_location_heads`,
                                            []
                                          ).map((locationHead: any, currentIndex: number) =>
                                            currentIndex === index
                                              ? {
                                                  ...locationHead,
                                                  city_id: getValue(e, `id`, ""),
                                                  city_name: getValue(
                                                    e,
                                                    `name`,
                                                    getValue(e, `label`, "")
                                                  ),
                                                }
                                              : locationHead
                                          ),
                                        })
                                      }
                                      lookup_id={getValue(item, `city_id`, "")}
                                      initial_selected_item={{
                                        id: getValue(item, `city_id`, ""),
                                        value: getValue(item, `city_id`, ""),
                                        label: getValue(item, `city_name`, ""),
                                        name: getValue(item, `city_name`, ""),
                                      }}
                                      placeholder={"Choose City"}
                                      lookup_api={"cities"}
                                      width={"100%"}
                                    />
                                  </div>
                                  <div className="department-location-head-field">
                                    <label className="checkpoint-active-details__label mb-1">
                                      Department Head
                                    </label>
                                    <SingleSelectSearchDropdown
                                      label={"label"}
                                      name={`head-${index}`}
                                      value={getValue(
                                        item,
                                        `head_user_id`,
                                        ""
                                      )}
                                      onChange={(e: any) =>
                                        handleLocationHeadChange(
                                          index,
                                          "head_user_id",
                                          getValue(e, `id`, "")
                                        )
                                      }
                                      lookup_id={getValue(
                                        item,
                                        `head_user_id`,
                                        ""
                                      )}
                                      placeholder={"Choose Department Head"}
                                      lookup_api={"users"}
                                      width={"100%"}
                                    />
                                  </div>
                                  <div
                                    className="department-location-head-remove"
                                    onClick={() =>
                                      handleRemoveLocationHead(index)
                                    }
                                  >
                                    <RemoveCircleSvgComponent color={"#fe7070"} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="department-location-head-empty">
                              No city-based department heads added yet.
                            </div>
                          )}

                          <div
                            className="custom_field_button_container mt-3"
                            onClick={handleAddLocationHead}
                          >
                            <img
                              className="custom_field_button_container_image"
                              src={`/images/add_blue.svg`}
                            />
                            <h6 className="custom_field_button_container_text">
                              Add Location Head
                            </h6>
                          </div>
                        </div>
                        <div className="user-details-wrapper__form-group form-group mt-3">
                          <label className="checkpoint-active-details__label">
                            Description
                          </label>
                          <InputField
                            inputType="TEXTAREA"
                            placeholder="Enter Description"
                            className="form-control w-100"
                            name="description"
                            id="description"
                            value={getValue(request, `description`, "")}
                            onChange={(e: any) => handleChangeText(e)}
                            label="Description"
                            // validator={simpleValidator}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            }
          </>
        )}
      </Layout>
      {/* <div className="map-vendor__footer w-100">
        <img src="/images/footer-image.svg" className="img-fluid" />
      </div> */}
    </>
  );
};

export default AdminUserControlDepartmentDetails;
